'use client'
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import s from './super.module.scss';
import LazyImage from '@/components/common/LazyImage';
import LazyVideo from '@/components/common/LazyVideo';
import { PortableText } from 'next-sanity';
import { portableBlockComponents } from '@/utils/portableText';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';


gsap.registerPlugin(ScrollTrigger);
interface HighlightProps {
    data: any;
}

export default function SuperMod({ data }: HighlightProps) {

    const [isMobile, setIsMobile] = useState(false);
    const superTextRef = useRef<HTMLDivElement>(null);
    const superRef = useRef<HTMLDivElement>(null);

    // Detectar si es mobile o desktop
    useEffect(() => {
        const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    useLayoutEffect(() => {
        const superEl = superRef.current;
        const textEl = superTextRef.current;
        if (!superEl || !textEl) return;

        const trigger = ScrollTrigger.create({
            trigger: superEl,                  // Usamos el contenedor principal (.super)
            start: "top 60px",                 // Cuando el contenedor llegue a 60px del top
            end: () => `+=${superEl.offsetHeight - 60}`,  // El pin se mantiene durante la altura del contenedor menos el offset
            pin: textEl,                       // Pinneamos el elemento con superTextRef
            pinSpacing: false,
            invalidateOnRefresh: true,
        });

        // Re-mide cuando cambia la altura del contenedor (resize, carga del media lazy)
        let lastSize = '';
        const observer = new ResizeObserver(() => {
            const size = `${superEl.offsetWidth}x${superEl.offsetHeight}`;
            if (size === lastSize) return;
            lastSize = size;
            ScrollTrigger.refresh();
        });
        observer.observe(superEl);

        return () => {
            observer.disconnect();
            trigger.kill();
        };
    }, []);



    return (
        <>
            <motion.div
                className={s.super}
                ref={superRef}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                viewport={{ once: true }}>
                <div className={s.superContent} ref={superTextRef}>
                    <div className={s.superText}>
                        <h2>{data.title}</h2>
                        {data.content &&
                            <PortableText
                                value={data.content}
                                components={portableBlockComponents(data.content)}
                            />
                        }
                    </div>
                </div>
                <div className={s.superImage}>
                    {data.background.mediaType == 'video' && data.background.video && data.background.video.videoUrl &&
                        <LazyVideo
                            src={isMobile && data.background.video.videoMobileUrl
                                ? data.background.video.videoMobileUrl
                                : data.background.video.videoUrl}
                            autoplay={true}
                            controls={false}
                            defaultInView={true}
                            alt={data.title || ""}
                        />}

                    {data.background.mediaType == 'image' && isMobile && data.background.imageMobile && data.background.imageMobile.image ? <LazyImage
                        src={data.background.imageMobile.image.imageUrl}
                        width={data.background.imageMobile.image.metadata.dimensions.width}
                        height={data.background.imageMobile.image.metadata.dimensions.height}
                        lqip={data.background.imageMobile.image.metadata.lqip || ""}
                        fill={false}
                        alt={data.background.imageMobile.image.attribution || ""}
                        caption={data.background.imageMobile.image.caption || null}
                        filename={data.background.imageMobile.image.filename || ""}
                    /> : data.background.image && data.background.image.image &&
                    <LazyImage
                        src={data.background.image.image.imageUrl}
                        width={data.background.image.image.metadata.dimensions.width}
                        height={data.background.image.image.metadata.dimensions.height}
                        lqip={data.background.image.image.metadata.lqip || ""}
                        fill={false}
                        alt={data.background.image.image.attribution || ""}
                        caption={data.background.image.image.caption || null}
                        filename={data.background.image.image.filename || ""}
                    />}
                </div>
            </motion.div>
        </>
    );
}